/**
 * "Yes, most days": days off and exceptions that override the weekly hours. Block a date or a range
 * (one blackout per date) or change the hours for one date (a window exception, which replaces that
 * day's weekly hours). Each change is saved as soon as it is submitted.
 */

import { useState } from 'react';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import {
  createAvailabilityException,
  deleteAvailabilityException,
  updateAvailabilityException,
  type AvailabilityException,
} from '../../../../lib/leasing.api';
import { LifecycleConfirmDialog } from '../shared';
import { groupExceptions, type ExceptionGroup } from './exception-groups';
import {
  DANGER_LINK_BUTTON_CLASS,
  FIELD_CLASS,
  InlineError,
  LINK_BUTTON_CLASS,
  ShowingsCard,
  usePanelReport,
  type PanelReport,
} from './panel-support';
import { TimeSelect } from './TimeSelect';
import { datesBetween, exceptionDateKey, formatShortDay, formatTimeRange, timeToMinutes } from './time-utils';
import { useAvailabilityAction } from './useAvailabilityAction';

const MAX_BLOCKED_RANGE_DAYS = 90;

interface ExceptionsPanelProps {
  listingId: string;
  isClosed: boolean;
  exceptions: AvailabilityException[];
  /** `YYYY-MM-DD` today in the listing's timezone: dates before it cannot be added. */
  today: string;
  report: PanelReport;
}

type FormState = {
  /** The group being edited, or null when adding. */
  editing: ExceptionGroup | null;
  kind: 'blackout' | 'window';
  date: string;
  endDate: string;
  startTime: string;
  endTime: string;
  notes: string;
};

function describeRange(group: ExceptionGroup): string {
  return group.from === group.to ? formatShortDay(group.from) : `${formatShortDay(group.from)} – ${formatShortDay(group.to)}`;
}

export function ExceptionsPanel({ listingId, isClosed, exceptions, today, report }: ExceptionsPanelProps) {
  const { run, saving, error, clearError } = useAvailabilityAction(listingId);
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<ExceptionGroup | null>(null);
  usePanelReport(report, saving, form !== null);

  const groups = groupExceptions(exceptions);
  const existingByDate = new Map(exceptions.map((ex) => [`${exceptionDateKey(ex.date)}:${ex.kind}`, ex]));

  const openAdd = (kind: 'blackout' | 'window') => {
    clearError();
    setFormError(null);
    setForm({ editing: null, kind, date: '', endDate: '', startTime: '09:00', endTime: '17:00', notes: '' });
  };

  const openEdit = (group: ExceptionGroup) => {
    clearError();
    setFormError(null);
    setForm({
      editing: group,
      kind: group.kind,
      date: group.from,
      endDate: group.to === group.from ? '' : group.to,
      startTime: group.startTime ?? '09:00',
      endTime: group.endTime ?? '17:00',
      notes: group.notes ?? '',
    });
  };

  const validate = (f: FormState): string | null => {
    if (f.editing) {
      if (f.kind === 'window' && timeToMinutes(f.endTime) <= timeToMinutes(f.startTime)) {
        return 'The end time must be after the start time.';
      }
      return null;
    }
    if (!f.date) return 'Choose a date.';
    if (f.date < today) return 'Choose today or a later date.';
    const last = f.kind === 'blackout' && f.endDate ? f.endDate : f.date;
    if (last < f.date) return 'The last day must be on or after the first day.';
    const days = datesBetween(f.date, last);
    if (days.length > MAX_BLOCKED_RANGE_DAYS) return `Block at most ${MAX_BLOCKED_RANGE_DAYS} days at a time.`;
    if (f.kind === 'window') {
      if (timeToMinutes(f.endTime) <= timeToMinutes(f.startTime)) return 'The end time must be after the start time.';
      if (existingByDate.has(`${f.date}:window`)) return 'That date already has changed hours. Edit it instead.';
      if (existingByDate.has(`${f.date}:blackout`)) return 'That date is blocked. Remove the block first.';
    } else {
      const clash = days.find((d) => existingByDate.has(`${d}:window`));
      if (clash) return `${formatShortDay(clash)} has changed hours. Remove them first.`;
    }
    return null;
  };

  const submit = async () => {
    if (!form) return;
    const problem = validate(form);
    if (problem) {
      setFormError(problem);
      return;
    }
    setFormError(null);
    const notes = form.notes.trim() || null;
    const ok = await run(async () => {
      if (form.editing) {
        for (const id of form.editing.ids) {
          await updateAvailabilityException(listingId, id, {
            ...(form.kind === 'window' ? { startTime: form.startTime, endTime: form.endTime } : {}),
            notes,
          });
        }
        return;
      }
      if (form.kind === 'window') {
        await createAvailabilityException(listingId, {
          date: form.date,
          kind: 'window',
          startTime: form.startTime,
          endTime: form.endTime,
          notes,
        });
        return;
      }
      const days = datesBetween(form.date, form.endDate || form.date);
      for (const date of days) {
        if (existingByDate.has(`${date}:blackout`)) continue;
        await createAvailabilityException(listingId, { date, kind: 'blackout', startTime: null, endTime: null, notes });
      }
    }, 'Failed to save the exception');
    if (ok) setForm(null);
  };

  const remove = async () => {
    if (!confirmRemove) return;
    const ok = await run(async () => {
      for (const id of confirmRemove.ids) await deleteAvailabilityException(listingId, id);
    }, 'Failed to remove the exception');
    if (ok) setConfirmRemove(null);
  };

  return (
    <ShowingsCard
      data-testid="exceptions-panel"
      title="Days off and exceptions"
      description="These override your weekly hours."
      action={
        !isClosed && (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid="add-exception-button"
              disabled={saving}
              onClick={() => openAdd('blackout')}
            >
              Block a date or range
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="exceptions-change-hours-button"
              disabled={saving}
              onClick={() => openAdd('window')}
            >
              Change hours for a date
            </Button>
          </div>
        )
      }
    >
      {error && !form && !confirmRemove && (
        <div className="mb-3">
          <InlineError message={error} testId="exceptions-error" />
        </div>
      )}

      {form && (
        <form
          data-testid={form.editing ? `edit-exception-form-${form.editing.ids[0]}` : 'add-exception-form'}
          className="mb-4 space-y-3 rounded-md border border-border bg-muted/20 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <p className="text-sm font-medium text-foreground">
            {form.editing
              ? `Edit ${describeRange(form.editing)}`
              : form.kind === 'blackout'
                ? 'Block a date or range'
                : 'Change hours for a date'}
          </p>
          {!form.editing && (
            <div className="flex flex-wrap gap-3">
              <div className="min-w-[10rem] flex-1">
                <label htmlFor="exception-date" className="mb-1 block text-xs font-medium text-foreground">
                  {form.kind === 'blackout' ? 'First day' : 'Date'}
                </label>
                <input
                  id="exception-date"
                  data-testid="exception-date"
                  type="date"
                  min={today}
                  value={form.date}
                  disabled={saving}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className={FIELD_CLASS}
                />
              </div>
              {form.kind === 'blackout' && (
                <div className="min-w-[10rem] flex-1">
                  <label htmlFor="exception-end-date" className="mb-1 block text-xs font-medium text-foreground">
                    Last day <span className="font-normal text-muted-foreground">(optional)</span>
                  </label>
                  <input
                    id="exception-end-date"
                    data-testid="exception-end-date"
                    type="date"
                    min={form.date || today}
                    value={form.endDate}
                    disabled={saving}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    className={FIELD_CLASS}
                  />
                </div>
              )}
            </div>
          )}
          {form.kind === 'window' && (
            <div className="flex flex-wrap gap-3">
              <TimeSelect
                id="exception-start"
                label="From"
                value={form.startTime}
                disabled={saving}
                data-testid="exception-start-time"
                onChange={(startTime) => setForm({ ...form, startTime })}
              />
              <TimeSelect
                id="exception-end"
                label="To"
                value={form.endTime}
                disabled={saving}
                data-testid="exception-end-time"
                onChange={(endTime) => setForm({ ...form, endTime })}
              />
            </div>
          )}
          <div>
            <label htmlFor="exception-notes" className="mb-1 block text-xs font-medium text-foreground">
              Note <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <input
              id="exception-notes"
              data-testid="exception-notes"
              type="text"
              value={form.notes}
              disabled={saving}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Thanksgiving"
              className={FIELD_CLASS}
            />
          </div>
          {(formError ?? error) && <InlineError message={(formError ?? error)!} testId="exception-form-error" />}
          <div className="flex gap-2">
            <Button type="submit" data-testid="exception-form-save" disabled={saving}>
              {saving ? 'Saving…' : form.editing ? 'Save changes' : form.kind === 'blackout' ? 'Block' : 'Save hours'}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="exception-form-cancel"
              disabled={saving}
              onClick={() => {
                setForm(null);
                setFormError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {groups.length === 0 ? (
        <p data-testid="exceptions-empty" className="text-sm text-muted-foreground">
          No days off or changed hours.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {groups.map((group) => {
            const id = group.ids[0]!;
            return (
              <li
                key={id}
                data-testid={`exception-row-${id}`}
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2"
              >
                <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold text-foreground">{describeRange(group)}</span>
                  {group.kind === 'blackout' ? (
                    <Badge variant="warning">Blocked</Badge>
                  ) : (
                    <>
                      <Badge variant="secondary">Changed hours</Badge>
                      <span className="text-muted-foreground">{formatTimeRange(group.startTime!, group.endTime!)}</span>
                    </>
                  )}
                  {group.notes && <span className="text-muted-foreground">{group.notes}</span>}
                </div>
                {!isClosed && (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      data-testid={`edit-exception-${id}`}
                      disabled={saving}
                      aria-label={`Edit ${describeRange(group)}`}
                      onClick={() => openEdit(group)}
                      className={LINK_BUTTON_CLASS}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      data-testid={`delete-exception-${id}`}
                      disabled={saving}
                      aria-label={`Remove ${describeRange(group)}`}
                      onClick={() => {
                        clearError();
                        setConfirmRemove(group);
                      }}
                      className={DANGER_LINK_BUTTON_CLASS}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <LifecycleConfirmDialog
        open={confirmRemove !== null}
        onOpenChange={(next) => {
          if (!next) setConfirmRemove(null);
        }}
        title={confirmRemove?.kind === 'window' ? 'Remove the changed hours?' : 'Remove this block?'}
        description={
          confirmRemove
            ? `${describeRange(confirmRemove)} will go back to your weekly hours.`
            : ''
        }
        confirmLabel="Remove"
        pendingLabel="Removing…"
        onConfirm={() => void remove()}
        isSubmitting={saving}
        destructive
        testIdPrefix="delete-exception-confirm"
      >
        {error && confirmRemove && <p className="mb-3 text-sm text-destructive" role="alert">{error}</p>}
      </LifecycleConfirmDialog>
    </ShowingsCard>
  );
}
